/**
 * Renders a policy version as a standalone public HTML page.
 *
 * The API uploads this output to Storage when a version is published, and the
 * admin editor previews the same output, so the preview matches what is
 * published. Raw HTML in the markdown is escaped and unsafe link schemes are
 * dropped, so a published page can never carry script.
 */

import { Marked } from 'marked';

const HTML_ESCAPES = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' };

export function escapeHtml(text) {
  return String(text ?? '').replace(/[&<>"']/g, ch => HTML_ESCAPES[ch]);
}

function isSafeUrl(href) {
  const value = String(href ?? '').trim();
  if (!value) return false;
  if (/^(https?:|mailto:|tel:)/i.test(value)) return true;
  // Relative links and in-page anchors carry no scheme.
  return !/^[a-z][a-z0-9+.-]*:/i.test(value);
}

const markdown = new Marked({
  gfm: true,
  renderer: {
    html({ text }) {
      return escapeHtml(text);
    },
    link(token) {
      if (isSafeUrl(token.href)) return false;
      return this.parser.parseInline(token.tokens);
    },
    image(token) {
      if (isSafeUrl(token.href)) return false;
      return escapeHtml(token.text);
    },
  },
});

/** Markdown to an HTML fragment (raw HTML escaped). */
export function renderPolicyMarkdown(contentMarkdown) {
  return markdown.parse(String(contentMarkdown ?? ''), { async: false });
}

/** "1 July 2026" in UTC, or '' for a missing or invalid date. */
export function formatPolicyDate(iso) {
  const date = iso ? new Date(iso) : null;
  if (!date || Number.isNaN(date.getTime())) return '';
  return new Intl.DateTimeFormat('en-GB', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(date);
}

const PAGE_STYLES = `
  :root { color-scheme: light; --ink: #1f2933; --muted: #5f6b7a; --rule: #e3e8ee; --link: #1d5fbf; }
  * { box-sizing: border-box; }
  body { margin: 0; background: #fff; color: var(--ink);
    font: 16px/1.65 -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif; }
  main { max-width: 760px; margin: 0 auto; padding: 40px 20px 56px; }
  header { border-bottom: 1px solid var(--rule); margin-bottom: 28px; padding-bottom: 16px; }
  header h1 { font-size: 1.9rem; line-height: 1.25; margin: 0 0 6px; }
  .meta { color: var(--muted); font-size: 0.92rem; margin: 0; }
  article h1 { font-size: 1.6rem; } article h2 { font-size: 1.3rem; margin-top: 2em; }
  article h3 { font-size: 1.1rem; margin-top: 1.6em; }
  article a { color: var(--link); }
  article table { border-collapse: collapse; width: 100%; margin: 1em 0; font-size: 0.95rem; }
  article th, article td { border: 1px solid var(--rule); padding: 6px 10px; text-align: left; vertical-align: top; }
  article blockquote { border-left: 3px solid var(--rule); color: var(--muted); margin: 1em 0; padding: 0 1em; }
  article code { background: #f4f6f8; border-radius: 4px; padding: 0.1em 0.35em; font-size: 0.9em; }
  article img { max-width: 100%; }
  footer { border-top: 1px solid var(--rule); color: var(--muted); font-size: 0.8rem; margin-top: 40px; padding-top: 14px; }
  footer code { word-break: break-all; }
  @page { margin: 18mm 16mm; }
  @media print {
    body { font-size: 11pt; }
    main { max-width: none; padding: 0; }
    article a { color: inherit; text-decoration: underline; }
    article h2, article h3 { break-after: avoid; }
    article table, article blockquote { break-inside: avoid; }
  }
`;

/**
 * A complete HTML document for one policy version.
 *
 * @param {object} args
 * @param {string} args.title
 * @param {string} args.version
 * @param {string} args.effectiveDate   ISO 8601
 * @param {string} args.contentMarkdown canonical markdown
 * @param {string} [args.contentHash]   SHA-256 of the canonical markdown
 */
export function renderPolicyHtml({ title, version, effectiveDate, contentMarkdown, contentHash }) {
  const effective = formatPolicyDate(effectiveDate);
  const meta = [`Version ${escapeHtml(version)}`, effective ? `Effective ${escapeHtml(effective)}` : '']
    .filter(Boolean)
    .join(' · ');
  const hashLine = contentHash
    ? `<p>Content fingerprint (SHA-256): <code>${escapeHtml(contentHash)}</code></p>`
    : '';

  return `<!doctype html>
<html lang="en">
<head>
<meta charset="utf-8">
<meta name="viewport" content="width=device-width, initial-scale=1">
<title>${escapeHtml(title)} (version ${escapeHtml(version)})</title>
${contentHash ? `<meta name="policy-content-hash" content="${escapeHtml(contentHash)}">` : ''}
<style>${PAGE_STYLES}</style>
</head>
<body>
<main>
<header>
<h1>${escapeHtml(title)}</h1>
<p class="meta">${meta}</p>
</header>
<article>
${renderPolicyMarkdown(contentMarkdown)}
</article>
<footer>
<p>WellboundPlus · ${escapeHtml(title)} · Version ${escapeHtml(version)}</p>
${hashLine}
</footer>
</main>
</body>
</html>
`;
}
